/**
 * SchedulerTab — recurring kickoffs for a room (SCHEDULER.md §7).
 *
 * North star: answer three questions at a glance — what's scheduled, is it on,
 * when did it last run — and let you flip any job on/off in one click. A
 * schedule is *a saved, deferred press of the room's Run button*; results stream
 * to the room as usual, so this tab shows JOB state (on/off, last fires, logs),
 * never results.
 *
 * Layout:
 *   - job-card list: green ● active / grey ○ paused / amber needs-attention,
 *     one-click power toggle, cadence in human words, ⋯ menu (Run now / Edit /
 *     Delete), and an expandable last-runs timeline.
 *   - "New schedule" opens the quick-schedule dialog (preset chips).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarClock, Power, StopCircle, MoreHorizontal, Play, Pencil, Trash2,
  ChevronDown, ChevronRight, Plus, Loader2, AlertTriangle,
} from 'lucide-react';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';
import Spinner from '@stepflow/ui-shared/components/Spinner.jsx';
import DeleteConnectionDialog from '@stepflow/ui-shared/components/secrets/DeleteConnectionDialog.jsx';
import AbortScheduleDialog from './AbortScheduleDialog.jsx';
import StartScheduleDialog from './StartScheduleDialog.jsx';
import {
  listPoolSchedules, createPoolSchedule, updatePoolSchedule,
  deletePoolSchedule, togglePoolSchedule, listPoolScheduleRuns, runPoolScheduleNow,
} from '@stepflow/ui-shared/services/poolSchedulesApi';
import { deriveScheduleUiState } from '@stepflow/ui-shared/utils/scheduleRunState.js';
import { humanizeCadence } from '@stepflow/ui-shared/utils/scheduleCadence.js';
import { humanizeMode, isLoopMode } from '@stepflow/ui-shared/utils/scheduleMode.js';
import { useAgentChannel, appsyncChannelScope } from '@stepflow/ui-shared/hooks/useAgentChannel';
import { AGENT_EVENTS, envelopeEventType } from '@stepflow/ui-shared/constants/agentEvents.js';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import ScheduleEditorDialog from './ScheduleEditorDialog.jsx';

const RUN_STATUS_STYLE = {
  STARTED: 'bg-emerald-100 text-emerald-700',
  ACHIEVED: 'bg-emerald-100 text-emerald-800',
  BUDGET_BLOCKED: 'bg-amber-100 text-amber-800',
  MAX_TURNS: 'bg-amber-100 text-amber-800',
  DEADLINE: 'bg-amber-100 text-amber-800',
  NO_PROGRESS: 'bg-amber-100 text-amber-800',
  KICKOFF_FAILED: 'bg-red-100 text-red-700',
  DISABLED_SKIP: 'bg-gray-100 text-gray-500',
};

function ScheduleCard({ schedule, canManage, networkId, poolId, liveRunning = false, onChanged, onEdit, onDelete }) {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [runs, setRuns] = useState(null);
  const [runsLoading, setRunsLoading] = useState(false);
  const [abortOpen, setAbortOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnRef = useRef(null);
  const sid = schedule.scheduleId;

  const loop = isLoopMode(schedule.mode);
  const cadenceText = useMemo(
    () => (loop ? humanizeMode(schedule) : humanizeCadence(schedule.cadence)),
    [loop, schedule],
  );
  // The card shows the NAME. Legacy rows created before names existed fall back
  // to the first prompt so they don't render blank.
  const displayName = (schedule.name && schedule.name.trim())
    || (loop ? (schedule.prompts && schedule.prompts[0]) : schedule.prompt)
    || '(unnamed schedule)';

  // A cadence schedule has a clock (its enabled flag = "clock armed"); a "Now"
  // / loop schedule is clock-less (enabled = armed, but a run only exists while
  // last-fire is non-terminal). This drives icon + dot + status via one helper.
  const isClock = !loop && !!(schedule.cadence && schedule.cadence.expr);
  const ui = useMemo(
    () => deriveScheduleUiState({
      enabled: !!schedule.enabled,
      isClock,
      lastFireStatus: schedule.lastFire?.status,
      liveRunning,
    }),
    [schedule.enabled, isClock, schedule.lastFire?.status, liveRunning],
  );

  const setEnabled = useCallback(async (next) => {
    setBusy(true);
    try {
      await togglePoolSchedule(networkId, poolId, sid, next);
      onChanged();
    } catch (e) {
      // surface minimally; list refresh will reconcile
      console.warn('toggle failed', e);
      onChanged();
      throw e;  // let the abort dialog show the error when it drove the call
    } finally {
      setBusy(false);
    }
  }, [networkId, poolId, sid, onChanged]);

  // The power control. BOTH directions confirm first — Start because it kicks
  // off a real run (spends compute/budget) and an accidental click shouldn't
  // launch work; Stop because disabling aborts the in-flight run. Neither fires
  // the API until the user confirms in the dialog.
  const onPrimaryAction = useCallback(() => {
    if (!canManage || busy) return;
    if (ui.buttonMode === 'STOP') {
      setAbortOpen(true);      // Stop → confirm
    } else {
      setStartOpen(true);      // Start → confirm
    }
  }, [canManage, busy, ui.buttonMode]);

  const confirmAbort = useCallback(async () => {
    await setEnabled(false);   // throws on failure → dialog shows it
    setAbortOpen(false);
  }, [setEnabled]);

  const confirmStart = useCallback(async () => {
    await setEnabled(true);    // throws on failure → dialog shows it
    setStartOpen(false);
  }, [setEnabled]);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const res = await listPoolScheduleRuns(networkId, poolId, sid, 10);
      setRuns(Array.isArray(res?.runs) ? res.runs : []);
    } catch {
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, [networkId, poolId, sid]);

  const toggleExpand = useCallback(() => {
    const next = !expanded;
    setExpanded(next);
    if (next && runs === null) loadRuns();
  }, [expanded, runs, loadRuns]);

  const runNow = useCallback(async () => {
    if (!canManage) return;
    try {
      await runPoolScheduleNow(networkId, poolId, sid);
    } catch (e) {
      console.warn('run-now failed', e);
    }
  }, [canManage, networkId, poolId, sid]);

  const needsAttention = ui.needsAttention;
  const lastFireReason = schedule.lastFire?.reason || schedule.lastFire?.error || '';

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center gap-3 px-3 py-2.5">
        <span
          className={`h-2.5 w-2.5 shrink-0 rounded-full ${ui.dotClass}`}
          title={ui.statusLabel || (schedule.enabled ? 'Active' : 'Paused')}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-gray-900" title={displayName}>
              {displayName}
            </span>
            {loop ? (
              <span className="shrink-0 rounded bg-indigo-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-indigo-700">
                Loop
              </span>
            ) : null}
            {needsAttention ? (
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
            ) : null}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500">
            <span title={schedule.cadence?.expr || (schedule.gate?.objective || '')}>{cadenceText}</span>
            {ui.statusLabel ? (
              <span
                className={
                  ui.tone === 'error' ? 'text-red-600 font-medium'
                    : ui.tone === 'warn' ? 'text-amber-600'
                      : ui.tone === 'running' ? 'text-emerald-600'
                        : 'text-gray-400'
                }
                title={lastFireReason || undefined}
              >
                · {ui.statusLabel}
                {lastFireReason ? ` — ${lastFireReason}`.slice(0, 80) : ''}
              </span>
            ) : schedule.lastFire?.at ? (
              <span className="text-gray-400">
                · last {String(schedule.lastFire.at || '').slice(0, 16).replace('T', ' ')}
              </span>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          onClick={toggleExpand}
          className="rounded p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-700"
          title="Show recent runs"
          data-testid={`schedule-expand-${sid}`}
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>

        {canManage ? (
          <button
            type="button"
            onClick={onPrimaryAction}
            disabled={busy}
            className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
              ui.buttonMode === 'STOP'
                ? 'border-red-200 text-red-700 hover:bg-red-50'
                : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
            }`}
            title={ui.buttonMode === 'STOP'
              ? (isClock ? 'Disarm schedule (stop future runs)' : 'Abort the running job')
              : (isClock ? 'Arm schedule' : 'Start a run now')}
            aria-label={ui.buttonMode === 'STOP' ? 'Stop schedule' : 'Run schedule'}
            data-testid={`schedule-toggle-${sid}`}
          >
            {busy ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>{ui.buttonMode === 'STOP' ? 'Stopping…' : 'Starting…'}</span>
              </>
            ) : ui.buttonMode === 'STOP' ? (
              <>
                <StopCircle className="h-3.5 w-3.5" />
                <span>{isClock ? 'Disarm' : 'Stop'}</span>
              </>
            ) : (
              <>
                <Power className="h-3.5 w-3.5" />
                <span>{isClock ? 'Arm' : 'Run'}</span>
              </>
            )}
          </button>
        ) : null}

        {canManage ? (
          <>
            <button
              ref={menuBtnRef}
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="inline-flex rounded p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-700"
              title="More actions"
              aria-label="More actions"
              data-testid={`schedule-menu-${sid}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
            <PortalMenu
              anchorRef={menuBtnRef}
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              width={160}
              className="rounded-md border border-gray-200 bg-white py-1 shadow-lg"
            >
              {/* While a run is in flight, Run now / Edit / Delete are disabled —
                  starting another run, mutating, or deleting mid-execution would
                  corrupt the live run. Stop it first (the power button). */}
              <button
                type="button"
                onClick={() => { setMenuOpen(false); runNow(); }}
                disabled={ui.isRunning}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                role="menuitem"
                title={ui.isRunning ? 'A run is in progress — stop it first' : undefined}
              >
                <Play className="h-3.5 w-3.5" /> Run now
              </button>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onEdit(schedule); }}
                disabled={ui.isRunning}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                role="menuitem"
                title={ui.isRunning ? 'Stop the run before editing' : undefined}
                data-testid={`schedule-edit-${sid}`}
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onDelete(schedule); }}
                disabled={ui.isRunning}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                role="menuitem"
                title={ui.isRunning ? 'Stop the run before deleting' : undefined}
                data-testid={`schedule-delete-${sid}`}
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </PortalMenu>
          </>
        ) : null}
      </div>

      {expanded ? (
        <div className="border-t border-gray-100 px-3 py-2">
          {runsLoading ? (
            <Spinner size="sm" label="Loading runs…" />
          ) : runs && runs.length ? (
            <ul className="space-y-1">
              {runs.map((r) => (
                <li key={r.firedAt} className="flex items-center gap-2 text-[11px]">
                  <span className={`rounded px-1 py-px font-medium ${RUN_STATUS_STYLE[r.status] || 'bg-gray-100 text-gray-600'}`}>
                    {r.status}
                  </span>
                  {r.iteration != null ? <span className="text-gray-400">#{r.iteration + 1}</span> : null}
                  {r.rating != null ? (
                    <span className="text-gray-600" title={r.reason || ''}>
                      {r.rating}/10{r.confidence != null ? ` @ ${r.confidence}` : ''}
                    </span>
                  ) : null}
                  <span className="text-gray-500">{String(r.firedAt || '').slice(0, 19).replace('T', ' ')}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-[11px] text-gray-400">No runs yet.</div>
          )}
        </div>
      ) : null}

      <AbortScheduleDialog
        open={abortOpen}
        scheduleName={displayName}
        onConfirm={confirmAbort}
        onClose={() => setAbortOpen(false)}
      />

      <StartScheduleDialog
        open={startOpen}
        scheduleName={displayName}
        isClock={!loop && !!(schedule.cadence && schedule.cadence.expr)}
        onConfirm={confirmStart}
        onClose={() => setStartOpen(false)}
      />
    </div>
  );
}

export default function SchedulerTab({ networkId, poolId, canManage = false }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const refresh = useCallback(async () => {
    if (!networkId || !poolId) return;
    setLoading(true);
    setError('');
    try {
      const res = await listPoolSchedules(networkId, poolId);
      setSchedules(Array.isArray(res?.schedules) ? res.schedules : []);
    } catch (e) {
      setError(e?.message || 'Failed to load schedules');
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, [networkId, poolId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Live status — no polling (app-wide policy). We subscribe to the SAME pool
  // channel the room uses (``default/agent/{scope}/{networkId}/{poolId}``); the
  // ``sfn_appsync_bridge`` publishes an SFN_TRANSITION / EXECUTION_COMPLETED /
  // UI_STATE event on every runtime execution's lifecycle. A JOB is MULTI-SFN
  // (one execution per episode) — each episode emits its own event carrying
  // ``scheduleId``/``loopIteration`` so we can attribute it to a card. We keep
  // the reaction simple + correct: on any run-lifecycle event for this pool,
  // re-pull the schedule list (a room has few schedules; one GET per event,
  // only while the tab is mounted). Event-driven, zero polling.
  const { user, activeTenantId } = useAuth();
  const channelScope = useMemo(
    () => appsyncChannelScope(activeTenantId, user?.sub),
    [activeTenantId, user?.sub],
  );
  const { messages: schedulerChannelMsgs } = useAgentChannel(networkId, channelScope, poolId);
  const lastHandledRef = useRef(null);
  // Authoritative live-run state for this pool, derived from the channel — used
  // to override a stale/clobbered denorm (e.g. a ROOM_BUSY_SKIP that overwrote a
  // STARTED). ``scheduleId`` on the SFN event links the live run to its card; a
  // bare UI_STATE (no scheduleId) still tells us the pool is busy. Rooms are
  // single-flight, so at most one schedule runs at a time.
  const [liveRun, setLiveRun] = useState({ running: false, scheduleId: '' });
  useEffect(() => {
    if (!Array.isArray(schedulerChannelMsgs) || schedulerChannelMsgs.length === 0) return;
    const env = schedulerChannelMsgs[schedulerChannelMsgs.length - 1];
    if (!env || env === lastHandledRef.current) return;
    const et = envelopeEventType(env);
    const payload = env.payload || {};
    const RUN_EVENTS = [AGENT_EVENTS.SFN_TRANSITION, AGENT_EVENTS.EXECUTION_COMPLETED,
      AGENT_EVENTS.EXECUTION_STARTED, AGENT_EVENTS.UI_STATE];
    if (!RUN_EVENTS.includes(et)) return;
    lastHandledRef.current = env;

    // Track pool run-state from the event so the card reflects reality.
    const status = String(payload.status || '').toUpperCase();
    const uiState = String(payload.uiState || '').toUpperCase();
    const started = et === AGENT_EVENTS.EXECUTION_STARTED
      || status === 'RUNNING' || uiState === 'RUNNING';
    const ended = et === AGENT_EVENTS.EXECUTION_COMPLETED
      || uiState === 'IDLE'
      || ['SUCCEEDED', 'FAILED', 'TIMED_OUT', 'ABORTED'].includes(status);
    if (started) {
      setLiveRun({ running: true, scheduleId: String(payload.scheduleId || payload.loopId || '') });
    } else if (ended) {
      setLiveRun({ running: false, scheduleId: '' });
    }

    refresh();  // re-pull; the denorm statuses may have changed server-side
  }, [schedulerChannelMsgs, refresh]);

  const openCreate = useCallback(() => { setEditing(null); setEditorOpen(true); }, []);
  const openEdit = useCallback((s) => { setEditing(s); setEditorOpen(true); }, []);

  const onSave = useCallback(async (cadenceBody) => {
    if (editing) {
      await updatePoolSchedule(networkId, poolId, editing.scheduleId, cadenceBody);
    } else {
      await createPoolSchedule(networkId, poolId, cadenceBody);
    }
    setEditorOpen(false);
    setEditing(null);
    refresh();
  }, [editing, networkId, poolId, refresh]);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    await deletePoolSchedule(networkId, poolId, pendingDelete.scheduleId);
    setPendingDelete(null);
    refresh();
  }, [pendingDelete, networkId, poolId, refresh]);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-gray-50">
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-2.5">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
          <CalendarClock className="h-4 w-4 text-gray-500" aria-hidden />
          Scheduler
        </div>
        {canManage ? (
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-700"
            data-testid="schedule-new"
          >
            <Plus className="h-3.5 w-3.5" /> New schedule
          </button>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {loading ? (
          <Spinner variant="panel" label="Loading…" />
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        ) : schedules.length === 0 ? (
          <div className="mt-8 text-center">
            <CalendarClock className="mx-auto h-8 w-8 text-gray-300" aria-hidden />
            <p className="mt-2 text-sm text-gray-500">No schedules yet — automate this room.</p>
            {canManage ? (
              <button
                type="button"
                onClick={openCreate}
                className="mt-3 inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                <Plus className="h-3.5 w-3.5" /> New schedule
              </button>
            ) : null}
          </div>
        ) : (
          <div className="space-y-2">
            {schedules.map((s) => (
              <ScheduleCard
                key={s.scheduleId}
                schedule={s}
                canManage={canManage}
                networkId={networkId}
                poolId={poolId}
                // Live override: this card is running if the pool has a live run
                // AND (the event named this schedule, OR it named none — a bare
                // UI_STATE — in which case single-flight means it's this pool's
                // one run; scope by the schedule that shows a STARTED denorm).
                liveRunning={liveRun.running && (
                  liveRun.scheduleId === s.scheduleId
                  || (!liveRun.scheduleId && String(s.lastFire?.status || '').toUpperCase() === 'STARTED')
                )}
                onChanged={refresh}
                onEdit={openEdit}
                onDelete={setPendingDelete}
              />
            ))}
          </div>
        )}
      </div>

      <ScheduleEditorDialog
        open={editorOpen}
        initial={editing}
        onSave={onSave}
        onClose={() => { setEditorOpen(false); setEditing(null); }}
      />

      <DeleteConnectionDialog
        open={Boolean(pendingDelete)}
        connectionName={pendingDelete?.prompt || 'this schedule'}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
